import { useEffect, useMemo, useState } from 'react';
import { apiClient } from '@copro/api';
import { useBreakpointDown, type StepTrackerStep } from '@all-manager/design-system-v2';
import { enqueueSnackbar } from '../../../../components/GlobalSnackbar/snackbarStore';
import { usePermissions } from '../../../../hooks/usePermissions';
import { useUpdateEmailStatus } from '../../api/useUpdateEmailStatus';
import type { InboxAttachmentRow, InboxEmail } from '../../types/inbox.types';
import { cleanEmailBodyText, isSignatureAttachment } from '../../utils/cleanEmailContent';
import {
  getWorkflowSteps,
  isWorkflowReadyToComplete,
  type WorkflowStepItem,
} from '../../utils/workflowSteps';

type Props = {
  open: boolean;
  email: InboxEmail | null;
  emails: InboxEmail[];
  totalEmails: number;
  hasMoreEmails: boolean;
  onLoadMoreEmails: () => Promise<InboxEmail[]>;
  spaceId: string;
  onClose: () => void;
  onSelectEmail: (email: InboxEmail) => void;
};

export function useEmailDetailModal(props: Props) {
  const { isAdmin } = usePermissions();
  const isMobile = useBreakpointDown('md');
  const { mutate: updateStatus, isPending: isUpdatingStatus } = useUpdateEmailStatus();

  const [viewMode, setViewMode] = useState<'html' | 'text'>('html');
  const [showSignatureAttachments, setShowSignatureAttachments] = useState(false);

  // Mobile Workflow states
  const [mobileView, setMobileView] = useState<'email' | 'workflow'>('email');
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [isEmailDrawerOpen, setIsEmailDrawerOpen] = useState(false);

  const email = props.email;
  const hasHtml = Boolean(email?.body_html?.trim());
  const activeViewMode: 'html' | 'text' = hasHtml && viewMode === 'html' ? 'html' : 'text';

  // Reset mobile workflow when email changes or modal closes/opens
  useEffect(() => {
    setMobileView('email');
    setActiveStepIndex(0);
    setIsEmailDrawerOpen(false);
  }, [props.email?.id, props.open]);

  const cleanedBodyText = useMemo(() => {
    return cleanEmailBodyText(email?.body_text);
  }, [email?.body_text]);

  const { relevantAttachments, signatureAttachments } = useMemo(() => {
    const all = email?.attachments || [];
    const relevant: InboxAttachmentRow[] = [];
    const signatures: InboxAttachmentRow[] = [];

    for (const att of all) {
      if (isSignatureAttachment(att)) {
        signatures.push(att);
      } else {
        relevant.push(att);
      }
    }

    return {
      relevantAttachments: relevant.length > 0 ? relevant : all,
      signatureAttachments: relevant.length > 0 ? signatures : [],
    };
  }, [email?.attachments]);

  // Dynamic workflow steps
  const workflowSteps = useMemo<WorkflowStepItem[]>(() => {
    return getWorkflowSteps(email);
  }, [email]);

  const isReadyToComplete = useMemo(() => {
    return isWorkflowReadyToComplete(email);
  }, [email]);

  const currentStep = useMemo(() => {
    if (workflowSteps.length === 0) return null;
    return workflowSteps[activeStepIndex] || workflowSteps[0];
  }, [workflowSteps, activeStepIndex]);

  const stepTrackerSteps = useMemo<StepTrackerStep[]>(() => {
    return workflowSteps.map((step, index) => ({
      id: step.id,
      label: step.shortTitle,
      completed: step.isCompleted && index !== activeStepIndex,
    }));
  }, [workflowSteps, activeStepIndex]);

  const isLastStep = activeStepIndex >= workflowSteps.length - 1;

  // Previous / Next email navigation
  const currentIndex = useMemo(() => {
    if (!email) return -1;
    return props.emails.findIndex((e) => e.id === email.id);
  }, [props.emails, email]);

  const hasPrev = currentIndex > 0;
  const isLastLoaded = currentIndex === props.emails.length - 1;
  // Past the last loaded email, the next one comes from the following page.
  const hasNext = currentIndex >= 0 && (!isLastLoaded || props.hasMoreEmails);

  function handlePrev() {
    if (hasPrev) {
      props.onSelectEmail(props.emails[currentIndex - 1]);
    }
  }

  async function handleNext() {
    if (!hasNext || !email) {
      return;
    }

    if (!isLastLoaded) {
      props.onSelectEmail(props.emails[currentIndex + 1]);
      return;
    }

    const emails = await props.onLoadMoreEmails();
    const next = emails[emails.findIndex((e) => e.id === email.id) + 1];
    if (next) {
      props.onSelectEmail(next);
    }
  }

  function handleToggleViewMode(mode: 'html' | 'text') {
    setViewMode(mode);
  }

  function handleToggleSignatureAttachments() {
    setShowSignatureAttachments((prev) => !prev);
  }

  function handleGoToWorkflow() {
    setMobileView('workflow');
    setActiveStepIndex(0);
  }

  function handleBackFromWorkflow() {
    if (activeStepIndex === 0) {
      setMobileView('email');
    } else {
      setActiveStepIndex((prev) => prev - 1);
    }
  }

  function handleNextStep() {
    if (activeStepIndex < workflowSteps.length - 1) {
      setActiveStepIndex((prev) => prev + 1);
    }
  }

  function handleSelectStep(index: number) {
    if (index >= 0 && index < workflowSteps.length) {
      setActiveStepIndex(index);
    }
  }

  function handleOpenEmailDrawer() {
    setIsEmailDrawerOpen(true);
  }

  function handleCloseEmailDrawer() {
    setIsEmailDrawerOpen(false);
  }

  function handleMarkAsProcessed() {
    if (!props.email) return;
    updateStatus(
      {
        emailId: props.email.id,
        spaceId: props.spaceId,
        status: 'processed',
      },
      {
        onSuccess: () => {
          if (hasNext) {
            handleNext();
          } else {
            props.onClose();
          }
        },
      }
    );
  }

  function handleMarkAsIgnored() {
    if (!props.email) return;
    updateStatus(
      {
        emailId: props.email.id,
        spaceId: props.spaceId,
        status: 'ignored',
      },
      {
        onSuccess: () => {
          if (hasNext) {
            handleNext();
          } else {
            props.onClose();
          }
        },
      }
    );
  }

  function handleRestore() {
    if (!props.email) return;
    updateStatus({
      emailId: props.email.id,
      spaceId: props.spaceId,
      status: 'pending',
    });
  }

  async function handleDownloadAttachment(att: InboxAttachmentRow) {
    try {
      const { data, error } = await apiClient.storage
        .from('inbox-attachments')
        .createSignedUrl(att.storage_path, 3600);

      if (error) throw error;
      if (data?.signedUrl) {
        window.open(data.signedUrl, '_blank');
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Impossible de télécharger la pièce jointe';
      enqueueSnackbar(msg, 'error');
    }
  }

  return {
    isAdmin,
    isMobile,
    email,
    isUpdatingStatus,
    activeViewMode,
    hasHtml,
    cleanedBodyText,
    relevantAttachments,
    signatureAttachments,
    showSignatureAttachments,
    hasPrev,
    hasNext,
    currentIndex,
    totalEmails: Math.max(props.totalEmails, props.emails.length),
    mobileView,
    activeStepIndex,
    workflowSteps,
    currentStep,
    stepTrackerSteps,
    isLastStep,
    isReadyToComplete,
    isEmailDrawerOpen,
    handlePrev,
    handleNext,
    handleToggleViewMode,
    handleToggleSignatureAttachments,
    handleGoToWorkflow,
    handleBackFromWorkflow,
    handleNextStep,
    handleSelectStep,
    handleOpenEmailDrawer,
    handleCloseEmailDrawer,
    handleMarkAsProcessed,
    handleMarkAsIgnored,
    handleRestore,
    handleDownloadAttachment,
  };
}
